import React, { useState, useRef } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { patientService, assureurService } from '../../services/api';
import { Patient, CreatePatientData, Assureur, PaginatedResponse } from '../../types';
import { Camera, Upload, X } from 'lucide-react';
import Webcam from 'react-webcam';
import PhoneInput from '../common/PhoneInput';
import { validerNumero } from '../../utils/phone';

interface PatientFormProps {
  patient?: Patient;
  onClose: () => void;
  onSuccess?: () => void;
}

const PatientForm: React.FC<PatientFormProps> = ({ patient, onClose, onSuccess }) => {
  const [showWebcam, setShowWebcam] = useState<boolean>(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [useAge, setUseAge] = useState<boolean>(false);  // Toggle entre date de naissance et âge
  const webcamRef = useRef<Webcam>(null);
  const queryClient = useQueryClient();

  const { register, handleSubmit, formState: { errors }, watch, setValue, control } = useForm<CreatePatientData>({
    defaultValues: patient ? {
      nom: patient.nom,
      prenoms: patient.prenoms,
      date_naissance: patient.date_naissance,
      genre: patient.genre,
      telephone: patient.telephone,
      email: patient.email || '',
      adresse: patient.adresse || '',
      profession: patient.profession || '',
      antecedents_medicaux: patient.antecedents_medicaux || '',
      allergies: patient.allergies || '',
      medicaments_actuels: patient.medicaments_actuels || '',
      assureur: patient.assureur,
      numero_assure: patient.numero_assure || '',
      nom_souscripteur: patient.nom_souscripteur || '',
    } : undefined
  });

  // Récupération des assureurs
  const { data: assuReursResponse } = useQuery<PaginatedResponse<Assureur>>({
    queryKey: ['assureurs'],
    queryFn: () => assureurService.getAll().then(res => res.data)
  });
  const assureurs = assuReursResponse?.results || [];

  // Mutation pour créer/modifier un patient
  const patientMutation = useMutation<Patient, Error, CreatePatientData>({
    mutationFn: (data) => patient 
      ? patientService.update(patient.id, data).then(res => res.data)
      : patientService.create(data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      onSuccess?.();
    }
  });

  // Mutation pour upload de la carte d'assurance
  const uploadMutation = useMutation<{ message: string }, Error, { id: number; photo: File }>({
    mutationFn: ({ id, photo }) => patientService.uploadCarteAssurance(id, photo).then(res => res.data)
  });

  const onSubmit: SubmitHandler<CreatePatientData> = async (data) => {
    try {
      // Nettoyer les données selon le mode choisi (âge ou date de naissance)
      // Enregistrer la fiche depuis l'accueil vaut vérification d'un dossier créé via WhatsApp
      const submitData: CreatePatientData = { ...data, a_verifier: false };

      if (useAge) {
        // Si on utilise l'âge, supprimer date_naissance et s'assurer que age_input est présent
        delete submitData.date_naissance;
        if (!submitData.age_input) {
          alert("Veuillez saisir l'âge du patient");
          return;
        }
      } else {
        // Si on utilise la date de naissance, supprimer age_input
        delete submitData.age_input;
        if (!submitData.date_naissance) {
          alert("Veuillez saisir la date de naissance du patient");
          return;
        }
      }

      const result = await patientMutation.mutateAsync(submitData);

      // Upload de la photo de carte si présente
      if (capturedImage && result) {
        const response = await fetch(capturedImage);
        const blob = await response.blob();
        const file = new File([blob], 'carte_assurance.jpg', { type: 'image/jpeg' });
        await uploadMutation.mutateAsync({ id: result.id, photo: file });
      }

      onSuccess?.();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const capturePhoto = (): void => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedImage(imageSrc);
      setShowWebcam(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setCapturedImage(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="max-w-4xl mx-auto son-panel p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {patient ? 'Modifier le patient' : 'Nouveau patient'}
        </h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700" title="Fermer">
          <X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Informations personnelles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom *
            </label>
            <input
              {...register('nom', { required: 'Le nom est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Entrez le nom du patient"
            />
            {errors.nom && <p className="text-red-500 text-sm mt-1">{errors.nom.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prénoms *
            </label>
            <input
              {...register('prenoms', { required: 'Les prénoms sont requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Entrez les prénoms du patient"
            />
            {errors.prenoms && <p className="text-red-500 text-sm mt-1">{errors.prenoms.message}</p>}
          </div>

          {/* Toggle entre Date de naissance et Âge */}
          <div className="col-span-2">
            <div className="flex gap-4 mb-2">
              <button
                type="button"
                onClick={() => setUseAge(false)}
                className={`px-4 py-2 rounded-md transition-colors ${
                  !useAge
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Date de naissance
              </button>
              <button
                type="button"
                onClick={() => setUseAge(true)}
                className={`px-4 py-2 rounded-md transition-colors ${
                  useAge
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Âge (approximatif)
              </button>
            </div>
            <p className="text-sm text-gray-600">
              {useAge
                ? "L'âge sera converti en date de naissance approximative (30 juin de l'année calculée)"
                : "Saisissez la date de naissance exacte du patient"}
            </p>
          </div>

          {useAge ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Âge (en années) *
              </label>
              <input
                type="number"
                {...register('age_input', {
                  required: useAge ? "L'âge est requis" : false,
                  min: { value: 0, message: "L'âge doit être positif" },
                  max: { value: 150, message: "Âge invalide" }
                })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                placeholder="Ex: 25"
                min="0"
                max="150"
              />
              {errors.age_input && <p className="text-red-500 text-sm mt-1">{errors.age_input.message}</p>}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de naissance *
              </label>
              <input
                type="date"
                {...register('date_naissance', { required: !useAge ? 'La date de naissance est requise' : false })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              />
              {errors.date_naissance && <p className="text-red-500 text-sm mt-1">{errors.date_naissance.message}</p>}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Genre *
            </label>
            <select
              {...register('genre', { required: 'Le genre est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            >
              <option value="">Sélectionner</option>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
            {errors.genre && <p className="text-red-500 text-sm mt-1">{errors.genre.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone *
            </label>
            <Controller
              name="telephone"
              control={control}
              rules={{ validate: validerNumero }}
              render={({ field }) => (
                <PhoneInput
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                />
              )}
            />
            {errors.telephone && <p className="text-red-500 text-sm mt-1">{errors.telephone.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              {...register('email')}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="patient@example.com"
              defaultValue=""
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profession
            </label>
            <input
              {...register('profession')}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Ex: Enseignant, Commerçant, Étudiant..."
              defaultValue=""
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adresse
          </label>
          <textarea
              {...register('adresse')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Adresse complète du patient"
              defaultValue=""
            />
        </div>

        {/* Bloc Assurance Médicale */}
        <div className="border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
          <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
            <span className="mr-2">🏥</span>
            Assurance Médicale
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assureur
              </label>
              <select
                {...register('assureur')}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              >
                <option value="">Sélectionner un assureur</option>
                {assureurs.map(assureur => (
                  <option key={assureur.id} value={assureur.id}>
                    {assureur.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro d'assuré
              </label>
              <input
                {...register('numero_assure')}
                placeholder="Numéro de carte d'assurance"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du souscripteur
              </label>
              <input
                {...register('nom_souscripteur')}
                placeholder="Nom complet du souscripteur de l'assurance"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              />
            </div>
          </div>

          {/* Photo de la carte d'assurance */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo de la carte d'assurance
            </label>
            
            <div className="flex flex-col space-y-4">
              {capturedImage && (
                <div className="relative">
                  <img 
                    src={capturedImage} 
                    alt="Carte d'assurance" 
                    className="max-w-xs h-auto border rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setCapturedImage(null)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                    title="Supprimer la photo"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setShowWebcam(true)}
                  className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Prendre une photo
                </button>

                <label className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  Télécharger
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Informations médicales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Antécédents médicaux
            </label>
            <textarea
              {...register('antecedents_medicaux')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Historique médical, opérations, maladies..."
              defaultValue=""
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allergies
            </label>
            <textarea
              {...register('allergies')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Allergies connues (médicaments, aliments...)"
              defaultValue=""
            />
          </div>
        </div>

        {/* Refus du serveur (ex. patient déjà enregistré sous ce nom et ce numéro) */}
        {patientMutation.isError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" role="alert">
            {(() => {
              const d = (patientMutation.error as any)?.response?.data;
              const msg = d?.non_field_errors ?? (d && typeof d === 'object' ? Object.values(d)[0] : null);
              const texte = Array.isArray(msg) ? msg[0] : msg;
              return typeof texte === 'string' && texte ? texte : "L'enregistrement a échoué.";
            })()}
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={patientMutation.isLoading}
            className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
          >
            {patientMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* Modal Webcam */}
      {showWebcam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <button onClick={() => setShowWebcam(false)} title="Fermer la webcam">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <Webcam
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              className="w-full mb-4 rounded"
            />
            
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowWebcam(false)}
                className="px-4 py-2 border border-gray-200 rounded-xl"
              >
                Annuler
              </button>
              <button
                onClick={capturePhoto}
                className="px-4 py-2 bg-blue-500 text-white rounded-md"
              >
                Capturer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientForm;